import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  createFilterOptions,
  FilterOptionsState,
} from "@mui/material";

export const SELECT_ALL_ID = -1;
export const SELECT_FILTERED_ID = -2;
// Marker name shared with ChipTag, which hides these rows from the chips
export const SELECT_ALL_NAME = "select-all";

type SelectAllMarker = {
  id: number;
  label: string;
  name: string;
};

// True for the "select all" / "select the filtered results" rows
export const isSelectAllOption = (option: unknown): option is SelectAllMarker =>
  typeof option === "object" &&
  option !== null &&
  (option as SelectAllMarker).name === SELECT_ALL_NAME;

// Plain booleans (no narrowing) for consumers whose option id type conflicts
// with the markers' numeric id
export const isSelectAllRow = (option: unknown) =>
  isSelectAllOption(option) && option.id === SELECT_ALL_ID;
export const isSelectFilteredRow = (option: unknown) =>
  isSelectAllOption(option) && option.id === SELECT_FILTERED_ID;

export type UseSelectAllOptionsParams<T> = {
  enabled: boolean;
  options: readonly T[];
  selected: readonly T[];
  inputValue: string;
  // Server-side search: the options lag behind the typed text while loading,
  // so the "filtered" row would target the previous result set
  loading?: boolean;
  getOptionKey: (option: T) => unknown;
  getOptionLabel: (option: T) => string;
  // Decides what "the filtered results" are; an identity filter (server-side
  // search) makes every current option a match
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  maxDisplayedOptions?: number;
  // Selection cap of the field: a "select all" row is only offered when the
  // whole candidate set fits in it
  maxSelected?: number;
};

/**
 * "Select all" / "select the filtered results" rows for a multiple Autocomplete.
 *
 * Without a search text, a "select all" row toggles every option. With one, a
 * "select the filtered results" row adds the matches to the selection (or
 * removes them). The rows are never part of `selected`: they only live in the
 * displayed options and in the Autocomplete value, where they render checked.
 */
export const useSelectAllOptions = <T>({
  enabled,
  options,
  selected,
  inputValue,
  loading = false,
  getOptionKey,
  getOptionLabel,
  filterOptions,
  maxDisplayedOptions,
  maxSelected,
}: UseSelectAllOptionsParams<T>) => {
  const intl = useIntl();

  const makeMarker = useCallback(
    (id: number, messageId: string): T => {
      const marker: SelectAllMarker = {
        id,
        label: intl.formatMessage({ id: messageId }),
        name: SELECT_ALL_NAME,
      };
      return marker as unknown as T;
    },
    [intl]
  );

  const selectAllMarker = useMemo(
    () => makeMarker(SELECT_ALL_ID, "SELECT_ALL"),
    [makeMarker]
  );

  const selectFilteredMarker = useMemo(
    () => makeMarker(SELECT_FILTERED_ID, "SELECT_ALL_FILTERED"),
    [makeMarker]
  );

  const selectedKeys = useMemo(
    () => new Set(selected.map(getOptionKey)),
    [getOptionKey, selected]
  );

  const matches = useMemo(() => {
    if (!enabled || inputValue === "") {
      return [];
    }
    const filter = filterOptions ?? createFilterOptions<T>();
    return filter([...options], { inputValue, getOptionLabel });
  }, [enabled, filterOptions, getOptionLabel, inputValue, options]);

  const cap = useCallback(
    (list: readonly T[]) =>
      maxDisplayedOptions === undefined
        ? [...list]
        : list.slice(0, maxDisplayedOptions),
    [maxDisplayedOptions]
  );

  const showSelectAllRow =
    enabled &&
    options.length > 1 &&
    (maxSelected === undefined || options.length <= maxSelected);

  const showSelectFilteredRow = useMemo(() => {
    if (!enabled || loading || matches.length === 0) {
      return false;
    }
    if (maxSelected === undefined) {
      return true;
    }
    const added = matches.filter(
      (match) => !selectedKeys.has(getOptionKey(match))
    ).length;
    return selected.length + added <= maxSelected;
  }, [
    enabled,
    getOptionKey,
    loading,
    matches,
    maxSelected,
    selected.length,
    selectedKeys,
  ]);

  const displayedOptions = useMemo(() => {
    if (!enabled) {
      return cap(options);
    }
    if (inputValue === "") {
      return showSelectAllRow
        ? [selectAllMarker, ...cap(options)]
        : cap(options);
    }
    return showSelectFilteredRow
      ? [selectFilteredMarker, ...cap(matches)]
      : cap(matches);
  }, [
    cap,
    enabled,
    inputValue,
    matches,
    showSelectAllRow,
    showSelectFilteredRow,
    options,
    selectAllMarker,
    selectFilteredMarker,
  ]);

  const autocompleteValue = useMemo(() => {
    if (!enabled) {
      return [...selected];
    }
    const markers: T[] = [];
    if (
      showSelectAllRow &&
      options.every((option) => selectedKeys.has(getOptionKey(option)))
    ) {
      markers.push(selectAllMarker);
    }
    if (
      showSelectFilteredRow &&
      matches.every((match) => selectedKeys.has(getOptionKey(match)))
    ) {
      markers.push(selectFilteredMarker);
    }
    return [...markers, ...selected];
  }, [
    enabled,
    getOptionKey,
    matches,
    showSelectAllRow,
    showSelectFilteredRow,
    options,
    selectAllMarker,
    selectFilteredMarker,
    selected,
    selectedKeys,
  ]);

  // Turns the Autocomplete's next value (markers included) into the real selection
  const resolveChange = useCallback(
    (
      nextValue: readonly T[],
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<T>
    ): T[] => {
      const real = nextValue.filter((option) => !isSelectAllOption(option));
      if (!enabled) {
        return real;
      }
      if (reason === "clear") {
        return [];
      }
      const option = details?.option;
      if (option === selectAllMarker) {
        return reason === "selectOption" ? [...options] : [];
      }
      if (option === selectFilteredMarker) {
        if (reason === "selectOption") {
          return [
            ...real,
            ...matches.filter(
              (match) => !selectedKeys.has(getOptionKey(match))
            ),
          ];
        }
        const matchKeys = new Set(matches.map(getOptionKey));
        return real.filter((item) => !matchKeys.has(getOptionKey(item)));
      }
      return real;
    },
    [
      enabled,
      getOptionKey,
      matches,
      options,
      selectAllMarker,
      selectFilteredMarker,
      selectedKeys,
    ]
  );

  // Some, but not all, of the rows a marker stands for are selected
  const isPartiallySelected = useCallback(
    (marker: T) => {
      const candidates = marker === selectAllMarker ? options : matches;
      const selectedCount = candidates.filter((option) =>
        selectedKeys.has(getOptionKey(option))
      ).length;
      return selectedCount > 0 && selectedCount < candidates.length;
    },
    [getOptionKey, matches, options, selectAllMarker, selectedKeys]
  );

  return {
    displayedOptions,
    autocompleteValue,
    resolveChange,
    isMarker: isSelectAllOption as (option: T) => boolean,
    markerLabel: (option: T) =>
      isSelectAllOption(option) ? option.label : undefined,
    isPartiallySelected,
  };
};
